import Axios, { AxiosInstance, AxiosResponse } from "axios";
import { Observable } from "rxjs";
import { AxiosObservableRequestConfig } from "src/core/models/api";

export class AxiosObservable<GlobalResponseType = any> {
  private config?: AxiosObservableRequestConfig;
  private axios: AxiosInstance;

  constructor(config?: AxiosObservableRequestConfig) {
    if (config) {
      this.config = config;
    }

    this.axios = Axios.create(this.config);
  }

  get interceptors() {
    return this.axios.interceptors;
  }

  static create<ResponseType = any>(config?: AxiosObservableRequestConfig) {
    return new AxiosObservable<ResponseType>(config);
  }

  request<ResponseType = GlobalResponseType>(
    config: AxiosObservableRequestConfig
  ) {
    return new Observable<AxiosResponse<ResponseType>>((destination) => {
      const cancelToken = Axios.CancelToken.source();

      let onUploadProgress;

      if (config.progressSubscriber) {
        onUploadProgress = (progress: any) => {
          if (config.progressSubscriber) {
            config.progressSubscriber.next(progress);
          }
        };
      }

      this.axios
        .request<ResponseType>({
          ...config,
          onUploadProgress,
          cancelToken: cancelToken.token,
        })
        .then((response) => {
          destination.next(response);
          destination.complete();
          config.progressSubscriber && config.progressSubscriber.complete();
        })
        .catch((reason) => {
          destination.error(reason);
          config.progressSubscriber && config.progressSubscriber.error(reason);
        });

      return () => {
        cancelToken.cancel();
      };
    });
  }

  get<ResponseType = GlobalResponseType>(
    url: string,
    config?: AxiosObservableRequestConfig
  ) {
    return this.request<ResponseType>({
      url: url,
      method: "GET",
      ...config,
    });
  }

  delete<ResponseType = GlobalResponseType>(
    url: string,
    config?: AxiosObservableRequestConfig
  ) {
    return this.request<ResponseType>({
      url: url,
      method: "DELETE",
      ...config,
    });
  }

  post<ResponseType = GlobalResponseType>(
    url: string,
    data?: any,
    config?: AxiosObservableRequestConfig
  ) {
    return this.request<ResponseType>({
      url: url,
      data: data,
      method: "POST",
      ...config,
    });
  }

  put<ResponseType = GlobalResponseType>(
    url: string,
    data?: any,
    config?: AxiosObservableRequestConfig
  ) {
    return this.request<ResponseType>({
      url: url,
      data: data,
      method: "PUT",
      ...config,
    });
  }

  patch<ResponseType = GlobalResponseType>(
    url: string,
    data?: any,
    config?: AxiosObservableRequestConfig
  ) {
    return this.request<ResponseType>({
      url: url,
      data: data,
      method: "PATCH",
      ...config,
    });
  }
}
